import { FC, useMemo } from 'react'

import { Dropdown } from '@revolut-internal/ui-kit'

import { CALENDAR_FILTER_MAX_HEIGHT } from 'modules/Monitoring/components/Filters/consts'
import { NestedDropdownItemBase } from 'modules/Monitoring/components/Filters/NestedDropdownItemBase'
import {
  RangeFilterContent,
  RangeFilterValue,
} from 'modules/Monitoring/components/Filters/RangeFilter/RangeFilterContent'
import { convertTimeStringToDate } from 'modules/Monitoring/utils/time/convertTimeStringToDate'
import { getTimeStringFromDate } from 'modules/Monitoring/utils/time/getTimeStringFromDate'

import { useAdditionalFilters } from '../AdditionalFiltersContext'
import { DUE_DATE_FILTER_KEY } from '../consts'
import { checkIfDateRangeValue } from '../utils/checkIfDateRangeValue'
import { createNextHoursPreset } from '../utils/datePresets'

type DueDateFilterProps = {
  open: boolean
  onClose: VoidFunction
}

const FILTER_PRESETS = [
  createNextHoursPreset(12),
  createNextHoursPreset(24),
  createNextHoursPreset(48),
]

export const DueDateFilter: FC<DueDateFilterProps> = ({ open, onClose }) => {
  const { additionalFilters, setAdditionalFilter, removeAdditionalFilters } =
    useAdditionalFilters()

  const rawValue = additionalFilters[DUE_DATE_FILTER_KEY]
  const dateRangeValue = checkIfDateRangeValue(rawValue) ? rawValue : null

  const dateRange = useMemo(() => {
    if (!dateRangeValue) {
      return undefined
    }

    return {
      timeRange: {
        from: dateRangeValue.from,
        to: dateRangeValue.to,
      },
      start: getTimeStringFromDate(dateRangeValue.from),
      end: getTimeStringFromDate(dateRangeValue.to),
    }
  }, [dateRangeValue])

  const handleCustomDateRangeChange = (value: RangeFilterValue | null) => {
    if (!value?.timeRange) {
      removeAdditionalFilters([DUE_DATE_FILTER_KEY])
      onClose()
      return
    }

    const { timeRange, start, end } = value

    const fromTimestamp =
      convertTimeStringToDate({
        timeStr: start,
        baseDate: new Date(timeRange.from),
        shouldReturnDate: true,
      })?.getTime() ?? timeRange.from

    const toTimestamp =
      convertTimeStringToDate({
        timeStr: end,
        baseDate: new Date(timeRange.to),
        shouldReturnDate: true,
      })?.getTime() ?? timeRange.to

    setAdditionalFilter(DUE_DATE_FILTER_KEY, {
      from: fromTimestamp,
      to: toTimestamp,
    })

    onClose()
  }

  return (
    <NestedDropdownItemBase
      label="Due date"
      renderValue={() => (dateRangeValue ? '1' : '')}
    >
      {(isShown, anchorRef) =>
        isShown && (
          <Dropdown
            open={open}
            anchorRef={anchorRef}
            maxHeight={CALENDAR_FILTER_MAX_HEIGHT}
            onClick={e => e.stopPropagation()}
          >
            <RangeFilterContent
              labelEmptyValue="Select date"
              timeRange={dateRange}
              presets={FILTER_PRESETS}
              disabledDays={{ before: new Date() }}
              supportsTime
              onSave={handleCustomDateRangeChange}
            />
          </Dropdown>
        )
      }
    </NestedDropdownItemBase>
  )
}
